import * as React from 'react';
import classnames from 'classnames';
import { st, classes } from './Button.st.css';
import { CoreButtonNext as ButtonNext } from '../internal/WixUiCoreComponents/components/CoreButtonNext';
import { useTPAComponentsContext } from '../TPAComponentsConfig';
import { deprecationLog } from '../../common/deprecationLog';
import {
  AffixTypes,
  ButtonProps,
  HoverStyles,
  PaddingModes,
  ButtonSize,
  FontConfig,
} from './types';
import { fontConfigMap } from './constants';

const wrapAffix = (icon: React.ReactElement, type: AffixTypes) => (
  <span className={classnames(classes[`${type}-icon`], classes.affixIcon)}>
    {icon}
  </span>
);

const getFontConfig = (
  size: ButtonSize = ButtonSize.medium,
  paddingMode: PaddingModes,
  mobile: boolean,
): FontConfig => {
  const deviceType = mobile ? 'mobile' : 'desktop';
  return fontConfigMap[paddingMode][size][deviceType];
};

const ButtonComponent = ({
  priority,
  size,
  fullWidth = false,
  innerRef,
  upgrade = false,
  wrapContent = false,
  paddingMode = upgrade ? PaddingModes.dynamicPaddings : PaddingModes.legacy,
  children,
  className,
  prefixIcon,
  suffixIcon,
  'data-hook': dataHook,
  hoverStyle = HoverStyles.underline,
  style,
  contentClassName = '',
  ...rest
}: ButtonProps) => {
  const { mobile } = useTPAComponentsContext();

  const affixes =
    prefixIcon || suffixIcon
      ? {
          prefixIcon: prefixIcon
            ? wrapAffix(prefixIcon, AffixTypes.prefix)
            : undefined,
          suffixIcon: suffixIcon
            ? wrapAffix(suffixIcon, AffixTypes.suffix)
            : undefined,
        }
      : null;

  React.useEffect(() => {
    if (!upgrade) {
      deprecationLog(
        'Button',
        'The current `Button` component API will be deprecated in a future version. Please use the `upgrade` prop in order to use the new API.',
      );
    }
  }, []);

  const fontConfig = getFontConfig(size, paddingMode, mobile);

  const dynamicStyle = {
    '--wix-ui-tpa-button-font-size-default': fontConfig.size,
    '--wix-ui-tpa-button-line-height-default': fontConfig.lineHeight,
    ...style,
  } as React.CSSProperties;

  return (
    <ButtonNext
      data-fullwidth={fullWidth}
      data-mobile={mobile}
      ref={innerRef}
      children={children}
      data-hook={dataHook}
      style={dynamicStyle}
      className={st(
        classes.root,
        {
          priority,
          size,
          fullWidth,
          mobile,
          paddingMode,
          wrapContent,
          hoverStyle,
        },
        classnames(className, { [classes.hasIcon]: !!affixes }),
      )}
      contentClassName={classnames(contentClassName, classes.content)}
      {...rest}
      {...affixes}
    />
  );
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref: React.RefObject<HTMLButtonElement>) => (
    <ButtonComponent {...props} innerRef={ref} />
  ),
);
